/**
 * FauxDS ProgressBar - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate ProgressBar component for prototyping.
 * It is NOT the real @faire/slate ProgressBar and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate ProgressBar, see: @faire/slate/components/progress-bar
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 29707:366 (Slate Progress Bar)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Success, Neutral
 * - Progress is a continuous 0–1 value (clamped), matching Slate
 * - Icon support verified (checkmark when complete)
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef, useMemo } from "react";

export type ProgressBarVariant = "success" | "neutral";

export interface ProgressBarProps {
  /**
   * Accessibility label for screen readers
   * @default "Label"
   */
  accessibilityLabel?: string;
  /**
   * Whether to show checkmark icon when progress is 100% (Success variant only)
   * @default true
   */
  showIcon?: boolean;
  /**
   * Progress from 0 (empty) to 1 (complete). Values outside that range are clamped.
   * @default 0
   */
  progress?: number;
  /**
   * Variant of the progress bar
   * @default "Success"
   */
  variant?: ProgressBarVariant;
  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
  /**
   * **playground only** — override fill color (e.g. Figma cart green)
   */
  fillColorOverride?: string;
}

// Checkmark icon (for completed state)
const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="8" cy="8" r="8" fill="currentColor" />
    <path
      d="M5 8L7 10L11 6"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * ProgressBar - FauxDS (recreated from Figma MCP)
 *
 * A progress bar is used to indicate progress of a task.
 * This implementation was created by extracting design specs directly from Figma.
 */
const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      accessibilityLabel = "Label",
      showIcon = true,
      progress = 0,
      variant = "success",
      className = "",
      fillColorOverride = undefined,
    },
    ref
  ) => {
    const progressValue = useMemo(() => {
      const n = progress ?? 0;
      if (!Number.isFinite(n)) {
        return 0;
      }
      return Math.max(0, Math.min(1, n));
    }, [progress]);

    const percentWidth = `${progressValue * 100}%`;

    // Fill colors based on variant
    const fillColor =
      fillColorOverride != null
        ? fillColorOverride
        : variant === "success"
          ? "var(--slate-color-green-700)" // Dark green for Success
          : "var(--slate-color-surface-primary-inverse)"; // Dark gray/black for Neutral

    // Show checkmark when progress is 100%, showIcon is true, and variant is Success
    const showCheckmark =
      variant === "success" && showIcon && progressValue === 1;

    return (
      <div
        ref={ref}
        className={cn("flex w-full items-center", className)}
        role="progressbar"
        aria-label={accessibilityLabel}
        aria-valuenow={Math.round(progressValue * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        {/* Progress bar track */}
        <div
          className="relative h-[8px] w-full overflow-hidden rounded-full bg-[var(--slate-color-grey-400)]"
          style={{
            borderRadius: "var(--slate-radius-component-round)",
          }}
        >
          {/* Progress bar fill */}
          <div
            className="absolute inset-0 rounded-full transition-[width] duration-300 ease-in-out"
            style={{
              width: percentWidth,
              backgroundColor: fillColor,
              borderRadius: "var(--slate-radius-component-round)",
            }}
          />
        </div>

        {/* Optional checkmark icon */}
        {showCheckmark ? (
          <div
            className="flex items-center pl-[var(--slate-dimensions-1)] transition-all delay-150 duration-300"
            style={{
              color: fillColor,
            }}
          >
            <CheckIcon />
          </div>
        ) : null}
      </div>
    );
  }
);

ProgressBar.displayName = "ProgressBar";

export default ProgressBar;
